'use client';

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ChevronLeft, ChevronRight, Trophy } from 'lucide-react';
import { AvatarWithFlair, LEVEL_COLORS } from '@/components/prelaunch/avatar-with-flair';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn, formatUSD, truncateAddress } from '@/lib/utils';
import { useWallet, useTakeoverPrice, useReferralCode } from '@/hooks';
import { sumV2GridSeatsToUsdc6 } from '@/lib/leaderboard/v2-tile-value';
import { getColors } from '@/lib/avatar';
import { useProfileModalOptional } from '@/contexts/profile-modal-context';
import { useLeaderboardModalOptional } from '@/contexts/leaderboard-modal-context';
import { useV2TileLeaderboard, type V2LeaderboardUser } from './use-v2-tile-leaderboard';

const USERS_PER_PAGE = 50;

interface LeaderboardEntry {
  rank: number;
  address: string;
  displayName: string;
  avatar?: string;
  level: number;
  seatsHeld: number;
  totalSeatValue: bigint;
  isCurrentUser?: boolean;
}

/**
 * V2 grid seats only (USDC 6dp micro). Uses `type=v2` API `totalSeatValue` when
 * present, else re-sums `gridSeats` (no V1 pool seats).
 */
function tileValueV2Micro(user: V2LeaderboardUser, takeoverPriceUsd: number): bigint {
  const raw = user.totalSeatValue;
  if (raw != null && raw !== '') {
    try {
      const n = BigInt(String(raw));
      if (n > 0n) return n;
    } catch {
      /* fall through */
    }
  }
  return sumV2GridSeatsToUsdc6(user.gridSeats || [], takeoverPriceUsd);
}

function transformUser(
  user: V2LeaderboardUser,
  rank: number,
  currentAddress: string | undefined,
  takeoverPriceUsd: number,
): LeaderboardEntry {
  const totalSeatValue = tileValueV2Micro(user, takeoverPriceUsd);
  const addr = user.address || user.id || '';
  return {
    rank,
    address: addr,
    displayName: user.displayName || `${addr.slice(0, 8)}...`,
    avatar: user.avatarUrl,
    level: user.level ?? 0,
    seatsHeld: user.v2SeatCount ?? user.seatsHeld ?? 0,
    totalSeatValue,
    isCurrentUser: currentAddress?.toLowerCase() === addr.toLowerCase(),
  };
}

// Mobile drops the Lvl + dedicated Value columns and stacks the value
// under the player name (level still surfaces via `<AvatarWithFlair>`),
// freeing horizontal space so the player name doesn't squish to zero.
const ROW_GRID =
  'grid grid-cols-[1.75rem_2.25rem_minmax(0,1fr)_2.75rem] sm:grid-cols-[2.25rem_2.25rem_minmax(0,1fr)_2.75rem_2.25rem_5.5rem] items-center gap-2 sm:gap-3 min-w-0';

function rankTone(rank: number) {
  if (rank === 1) return 'text-amber-200';
  if (rank === 2) return 'text-zinc-200';
  if (rank === 3) return 'text-amber-600/90';
  return 'text-zinc-500';
}

function LeaderboardRow({ entry }: { entry: LeaderboardEntry }) {
  const profileModal = useProfileModalOptional();
  const leaderboardModal = useLeaderboardModalOptional();
  const avatarColor = entry.isCurrentUser ? getColors(entry.address).fg : undefined;

  const handleClick = () => {
    leaderboardModal?.closeLeaderboard();
    profileModal?.openProfile(entry.address);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        ROW_GRID,
        'w-full max-w-full rounded-[4px] border border-transparent px-2 py-2 text-left transition-colors',
        'hover:border-amber-500/20 hover:bg-amber-500/[0.04]',
        entry.isCurrentUser && 'border-amber-400/35 bg-amber-500/[0.07]',
      )}
      style={
        entry.isCurrentUser && avatarColor
          ? {
              boxShadow: `inset 0 0 0 1px ${avatarColor}40`,
            }
          : undefined
      }
    >
      <span
        className={cn('text-center text-xs font-mono tabular-nums font-semibold', rankTone(entry.rank))}
      >
        {entry.rank}
      </span>

      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex justify-center">
            <AvatarWithFlair
              avatarUrl={entry.avatar}
              address={entry.address}
              level={entry.level}
              size="sm"
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="space-y-1">
          <div className="text-xs font-semibold">{entry.displayName}</div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {truncateAddress(entry.address)}
          </div>
        </TooltipContent>
      </Tooltip>

      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium leading-tight text-zinc-100">
          {entry.displayName}
          {entry.isCurrentUser && (
            <span className="ml-1.5 text-[9px] font-mono uppercase tracking-[0.14em] text-amber-300/90">
              You
            </span>
          )}
        </div>
        <div className="sm:hidden mt-0.5 font-mono text-[11px] tabular-nums text-amber-100/95">
          {formatUSD(entry.totalSeatValue)}
        </div>
      </div>

      <div className="hidden sm:block text-center">
        <span
          className={cn('text-[11px] font-bold', LEVEL_COLORS[entry.level] || LEVEL_COLORS[0])}
        >
          {entry.level}
        </span>
      </div>

      <div className="text-center font-mono text-xs tabular-nums text-zinc-400">{entry.seatsHeld}</div>

      <div className="hidden sm:block text-right font-mono text-sm tabular-nums text-amber-100/95">
        {formatUSD(entry.totalSeatValue)}
      </div>
    </button>
  );
}

/**
 * Leaderboard body: V2 grid tile value only (USDC 6dp). Shell chrome is in `LeaderboardModal`.
 */
export function LeaderboardPageContent() {
  const wallet = useWallet();
  const { address } = wallet;
  const { priceUsd: takeoverPriceUsd } = useTakeoverPrice();
  const queryClient = useQueryClient();
  const referralCode = useReferralCode();
  const [page, setPage] = useState(1);
  const [registering, setRegistering] = useState(false);
  const registeredAddressRef = useRef<string | null>(null);

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const token = await wallet.getAccessToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [wallet]);

  const { data: preregResponse, refetch: refetchPrereg, isLoading: preregLoading } = useQuery({
    queryKey: ['prereg-status', wallet.address?.toLowerCase()],
    queryFn: async () => {
      if (!wallet.address) return null;
      const res = await fetch(`/api/preregister/${wallet.address}`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!wallet.address,
    staleTime: 10_000,
  });

  const isRegistered = preregResponse?.registered === true;

  useEffect(() => {
    if (!wallet.address || !wallet.isConnected) return;
    if (preregLoading) return;
    if (isRegistered || registering) return;
    if (registeredAddressRef.current === wallet.address.toLowerCase()) return;

    const register = async () => {
      setRegistering(true);
      try {
        const headers = await getAuthHeaders();
        const activeAddr = wallet.address!.toLowerCase();
        const regRes = await fetch('/api/preregister', {
          method: 'POST',
          headers,
          body: JSON.stringify({ referralCode: referralCode || undefined }),
        });
        if (regRes.ok) {
          registeredAddressRef.current = activeAddr;
          await refetchPrereg();
          queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
        }
      } catch (error) {
        console.error('Auto-registration failed:', error);
      } finally {
        setRegistering(false);
      }
    };

    register();
  }, [
    wallet.address,
    wallet.isConnected,
    isRegistered,
    preregLoading,
    registering,
    referralCode,
    refetchPrereg,
    queryClient,
    getAuthHeaders,
  ]);

  const { data: leaderboardUsers, isLoading, isError, refetch } = useV2TileLeaderboard();

  const allEntries: LeaderboardEntry[] = useMemo(() => {
    const entries: LeaderboardEntry[] = (leaderboardUsers ?? [])
      .map((user) => transformUser(user, 0, address || undefined, takeoverPriceUsd))
      .filter((e) => e.totalSeatValue > 0n);

    entries.sort((a, b) => {
      if (b.totalSeatValue > a.totalSeatValue) return 1;
      if (b.totalSeatValue < a.totalSeatValue) return -1;
      return 0;
    });
    entries.forEach((e, i) => {
      e.rank = i + 1;
    });
    return entries;
  }, [leaderboardUsers, address, takeoverPriceUsd]);

  const currentUser = allEntries.find((e) => e.isCurrentUser);

  const totalPages = Math.ceil(allEntries.length / USERS_PER_PAGE);
  const paginatedEntries = allEntries.slice((page - 1) * USERS_PER_PAGE, page * USERS_PER_PAGE);

  return (
    <div className="space-y-4">
      {currentUser && (
        <div
          className="rounded-[4px] border border-amber-500/20 bg-black/35 px-3 py-3 sm:px-4"
          style={{
            borderColor: `${getColors(currentUser.address).fg}55`,
            backgroundColor: `${getColors(currentUser.address).fg}12`,
          }}
        >
          <div className="flex flex-wrap items-center gap-3 sm:gap-5">
            <AvatarWithFlair
              avatarUrl={currentUser.avatar}
              address={currentUser.address}
              level={currentUser.level}
              size="md"
            />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">Your rank</p>
              <p className="truncate text-sm font-semibold text-zinc-100">{currentUser.displayName}</p>
            </div>
            <div className="flex flex-wrap items-end gap-4 sm:gap-6">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">#</p>
                <p className="font-mono text-lg font-semibold tabular-nums text-amber-100">
                  {currentUser.rank}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Tiles</p>
                <p className="font-mono text-lg font-semibold tabular-nums text-zinc-200">
                  {currentUser.seatsHeld}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Tile value</p>
                <p className="font-mono text-lg font-semibold tabular-nums text-amber-100/95">
                  {formatUSD(currentUser.totalSeatValue)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isError ? (
        <div className="rounded-[4px] border border-dashed border-amber-500/25 bg-black/25 py-10 px-4 text-center">
          <p className="text-sm text-zinc-300">Couldn&apos;t load the leaderboard.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 border-amber-500/30 text-amber-100 hover:bg-amber-500/10"
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => (
            <Skeleton
              key={i}
              className="h-[52px] w-full rounded-[4px] border border-amber-500/10 bg-amber-950/20"
            />
          ))}
        </div>
      ) : paginatedEntries.length > 0 ? (
        <div>
          <div
            className={cn(
              ROW_GRID,
              'mb-1 border-b border-amber-500/15 px-2 pb-2 text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500',
            )}
          >
            <span className="text-center">#</span>
            <span />
            <span>Player</span>
            <span className="hidden sm:block text-center">Lvl</span>
            <span className="text-center">Tiles</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="hidden sm:block cursor-help text-right underline decoration-dotted decoration-amber-500/30 underline-offset-[3px]">
                  Value <ArrowDown className="inline h-2.5 w-2.5" aria-hidden />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px] space-y-1.5">
                <p className="text-[11px] font-semibold text-amber-100">
                  Ranked by V2 grid tile value
                </p>
                <p className="text-[10px] leading-relaxed text-zinc-400">
                  Sum of every V2 seat&apos;s self-assessed price, normalized to USD.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="space-y-0.5">
            {paginatedEntries.map((entry) => (
              <LeaderboardRow key={entry.address} entry={entry} />
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-[4px] border border-dashed border-amber-500/20 bg-black/25 py-14 text-center">
          <Trophy className="mx-auto mb-3 h-8 w-8 text-amber-500/40" />
          <p className="text-sm text-zinc-400">No ranked players yet.</p>
          <p className="mt-1 text-xs text-zinc-600">Hold V2 grid tiles to appear here.</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-amber-500/10 pt-3">
          <p className="text-[11px] font-mono text-zinc-500">
            {(page - 1) * USERS_PER_PAGE + 1}–{Math.min(page * USERS_PER_PAGE, allEntries.length)} of{' '}
            {allEntries.length}
          </p>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 border-amber-500/20 bg-transparent text-zinc-300 hover:bg-amber-500/10"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[5.5rem] text-center font-mono text-[11px] text-zinc-400">
              {page} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 border-amber-500/20 bg-transparent text-zinc-300 hover:bg-amber-500/10"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
